import { motion } from 'motion/react';

import { useEffect, useState } from 'react';

import { ArrowLeft, Star, ChevronRight, CheckCircle2, Bot } from 'lucide-react';

import { ImageWithFallback } from './ImageWithFallback';

import { ImageModal } from './ImageModal';

import { VideoSection } from './VideoSection';

import { completeProductContent } from '../data/productContent';

import { parseMarkdownText } from '../utils/textUtils';



type ProductDetailProps = {

  product: {

    title: string;

    subtitle?: string;

    tag?: string;

    desc: string;

    logo: string;

    gradient: string;

    buttonBg: string;

    shadow: string;

    list?: Array<{ label: string; href?: string }>;

  };

  onBack: () => void;

  onContact: () => void;

};



const getProductContent = (title: string) => {

  return completeProductContent[title as keyof typeof completeProductContent] || { overview: "", sections: [] };

};



const DetailSection = ({ section, index, onContact }: { section: any, index: number, onContact: () => void }) => {

  const [modalImage, setModalImage] = useState<string | null>(null);



  // Define different background colors for each product section

  const backgroundColors = [

    'bg-gradient-to-br from-blue-100 to-indigo-900',

    'bg-gradient-to-br from-green-50 to-emerald-100', 

    'bg-gradient-to-br from-purple-50 to-violet-100',

    'bg-gradient-to-br from-orange-50 to-amber-100',

    'bg-gradient-to-br from-pink-50 to-rose-100',

    'bg-gradient-to-br from-cyan-50 to-teal-100',

    'bg-gradient-to-br from-yellow-50 to-orange-100',

    'bg-gradient-to-br from-red-50 to-pink-100',

    'bg-gradient-to-br from-slate-50 to-gray-100'

  ];



  // Product URLs mapping

  const productUrls: { [key: string]: string } = {

    'chutki': 'https://translation.aicte-india.org/translation-independent/Text-Editor/',

    'bujji': 'https://translation.aicte-india.org/translation-independent/',

    'ananta': 'https://ananta.aicte-india.org/',

    'voice-forms': 'https://anuvadini.aicte-india.org/voiceform/',

    'virtual-keyboard': 'https://translation.aicte-india.org/translation-independent/transliterate/',

    'video-translation': 'https://translation.aicte-india.org/translation-independent/PmModiNationalInternshipPortal/',

    'government-schemes': 'https://anuvadini.aicte-india.org/govtofindiascheme/',

    'dictation-tool': 'https://anuvadini.aicte-india.org/dictation-tool/',

    'legal-suite': 'https://anuvadini.aicte-india.org/lawdictionary/',

    'bhasha-daan': 'https://anuvadini.aicte-india.org/Bhasha-daan/',

    'prerana': 'https://prerana.education.gov.in/',

    'yuva-sangam': 'https://ebsb.aicte-india.org/',

    'aicte-internship': 'https://internship.aicte-india.org/',

    'eco-clubs': 'https://ecoclubs.education.gov.in/'

  };



  const sectionBg = backgroundColors[index % backgroundColors.length];



  return (

    <section

      id={section.id}

      className={`py-8 sm:py-12 md:py-16 lg:py-20 px-3 sm:px-4 md:px-6 overflow-hidden relative ${sectionBg}`}

    >

      <div className={`max-w-7xl mx-auto flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-start gap-6 sm:gap-8 md:gap-12 lg:gap-16`}>

        <motion.div

          initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}

          whileInView={{ opacity: 1, x: 0 }}

          viewport={{ once: true, margin: "-100px" }}

          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}

          className="flex-1 space-y-4 sm:space-y-6 md:space-y-8 text-center lg:text-left"

        >

          <div className="space-y-4 sm:space-y-6 md:space-y-8">

            <div className={`inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gray-800/90 border-gray-700/40 backdrop-blur-xl shadow-xl rounded-full border`}>

              <Star className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 fill-current text-white animate-pulse`} />

              <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider text-white`}>Detailed Solution</span>

            </div>



            <div className="flex flex-col gap-4 sm:gap-6 md:gap-8">

              {section.logo && (

                <div className="bg-gray-100/90 rounded-2xl sm:rounded-[2rem] p-3 sm:p-4 md:p-5 w-full max-w-[180px] sm:max-w-[220px] md:max-w-[280px] mx-auto lg:mx-0 shadow-2xl border border-gray-200/40 transform hover:scale-105 transition-all duration-500">

                  <img src={section.logo} alt={`${section.title} Logo`} className="w-full h-20 sm:h-24 md:h-28 lg:h-32 xl:h-36 object-contain rounded-lg sm:rounded-xl" />

                </div>

              )}

              {productUrls[section.id] ? (

                <div className="space-y-2">

                  <a 

                    href={productUrls[section.id]} 

                    target="_blank" 

                    rel="noopener noreferrer"

                    className="block hover:scale-105 transition-transform duration-300"

                  >

                    <h2 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-800 hover:text-blue-600 leading-tight tracking-tight cursor-pointer transition-colors duration-300`} style={{ textShadow: '0 8px 25px rgba(0,0,0,0.1)' }}>

                      {section.title}

                    </h2>

                  </a>

                  <a

                    href={productUrls[section.id]}

                    target="_blank"

                    rel="noopener noreferrer"

                    className="text-xs sm:text-sm md:text-base text-gray-600 hover:text-blue-600 transition-colors duration-300 break-all"

                  >

                    <span className="font-semibold text-gray-700">URL:</span> {productUrls[section.id]}

                  </a>

                </div>

              ) : (

                <h2 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-800 leading-tight tracking-tight`} style={{ textShadow: '0 8px 25px rgba(0,0,0,0.1)' }}>

                  {section.title}

                </h2>

              )}

            </div>

          </div>



          <div className={`text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed max-w-3xl font-medium text-justify-inter-word`}>

            {parseMarkdownText(section.description)}

          </div>



          {section.features && section.features.length > 0 && (

            <div className="space-y-3 sm:space-y-4 md:space-y-6">

              <h3 className={`text-base sm:text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2 justify-center lg:justify-start`}>

                <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600`} />

                Key Features

              </h3>

              <div className="grid gap-2 sm:gap-3 md:gap-4 max-w-4xl">

                {section.features.slice(0, 6).map((feature: string, i: number) => (

                  <motion.div

                    key={i}

                    initial={{ opacity: 0, x: -20 }}

                    whileInView={{ opacity: 1, x: 0 }}

                    viewport={{ once: true }}

                    transition={{ delay: i * 0.1, duration: 0.6 }}

                    className={`flex items-start gap-2 sm:gap-3 group bg-gray-100/80 border-gray-300 hover:bg-gray-200/90 backdrop-blur-md rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4 border transition-all duration-300`}

                  >

                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-600 group-hover:bg-gray-800 rounded-full flex-shrink-0 mt-1.5 sm:mt-2 transition-colors`} />

                    <span className={`text-gray-700 group-hover:text-gray-900 text-xs sm:text-sm md:text-base font-medium leading-relaxed text-justify-inter-word transition-colors`}>

                      {feature}

                    </span>

                  </motion.div>

                ))}

              </div>

            </div>

          )}



          {section.highlights && section.highlights.length > 0 && (

            <div className="space-y-3 sm:space-y-4 md:space-y-6">

              <h3 className={`text-base sm:text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2 justify-center lg:justify-start`}>

                <Star className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600 fill-current`} />

                Key Highlights

              </h3>

              <div className="grid gap-2 sm:gap-3 md:gap-4 max-w-4xl">

                {section.highlights.map((highlight: string, i: number) => (

                  <motion.div

                    key={i}

                    initial={{ opacity: 0, x: -20 }}

                    whileInView={{ opacity: 1, x: 0 }}

                    viewport={{ once: true }}

                    transition={{ delay: i * 0.1, duration: 0.6 }}

                    className={`flex items-start gap-2 sm:gap-3 group bg-gray-100/90 border-gray-300 hover:bg-gray-200/95 backdrop-blur-md rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4 border transition-all duration-300`}

                  >

                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-500 rounded-full flex-shrink-0 mt-1.5 sm:mt-2 group-hover:bg-yellow-400 transition-colors" />

                    <span className={`text-gray-700 group-hover:text-gray-900 text-xs sm:text-sm md:text-base font-medium leading-relaxed text-justify-inter-word transition-colors`}>

                      {highlight}

                    </span>

                  </motion.div>

                ))}

              </div>

            </div>

          )}



          {section.impact && section.impact.length > 0 && (

            <div className="space-y-3 sm:space-y-4 md:space-y-6">

              <h3 className={`text-base sm:text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2 justify-center lg:justify-start`}>

                <Bot className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600`} />

                Impact & Value

              </h3>

              <div className="grid gap-2 sm:gap-3 md:gap-4 max-w-4xl">

                {section.impact.map((impact: string, i: number) => (

                  <motion.div

                    key={i}

                    initial={{ opacity: 0, x: -20 }}

                    whileInView={{ opacity: 1, x: 0 }}

                    viewport={{ once: true }}

                    transition={{ delay: i * 0.1, duration: 0.6 }}

                    className={`flex items-start gap-2 sm:gap-3 group bg-gray-100/90 border-gray-300 hover:bg-gray-200/95 backdrop-blur-md rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4 border transition-all duration-300`}

                  >

                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full flex-shrink-0 mt-1.5 sm:mt-2 group-hover:bg-green-400 transition-colors" />

                    <span className={`text-gray-700 group-hover:text-gray-900 text-xs sm:text-sm md:text-base font-medium leading-relaxed text-justify-inter-word transition-colors`}>

                      {impact}

                    </span>

                  </motion.div>

                ))}

              </div>

            </div>

          )}



          {section.bestFor && (

            <div className={`bg-gray-100/80 border-gray-300 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 border max-w-3xl`}>

              <h4 className={`text-gray-800 font-bold mb-2 sm:mb-3 text-xs sm:text-sm md:text-base flex items-center justify-center lg:justify-start gap-2`}>

                <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 bg-gray-800 rounded-full`} />

                Best For

              </h4>

              <p className={`text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed text-justify-inter-word`}>

                {section.bestFor}

              </p>

              {section.id === 'voice-forms' && (

                <div className="mt-3 sm:mt-4">

                  <a 

                    href="https://anuvadini.aicte-india.org/voice-bank-form" 

                    target="_blank" 

                    rel="noopener noreferrer"

                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm underline underline-offset-2 transition-colors"

                  >

                    Click to View Simple Bank Registration Form

                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />

                    </svg>

                  </a>

                </div>

              )}

            </div>

          )}



          <button

            onClick={onContact}

            className="group bg-green-400 hover:bg-green-500 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-xs sm:text-sm md:text-base lg:text-lg transition-all duration-300 shadow-xl flex items-center gap-2 sm:gap-3 hover:scale-105 active:scale-95 w-full sm:w-auto justify-center"

          >

            Contact Anuvadini

            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform duration-300" />

          </button>

        </motion.div>



        <motion.div

          initial={{ opacity: 0, scale: 0.9, x: index % 2 === 0 ? 60 : -60 }}

          whileInView={{ opacity: 1, scale: 1, x: 0 }}

          viewport={{ once: true, margin: "-100px" }}

          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}

          className="flex-1 relative max-w-2xl w-full"

        >

          <div className="space-y-4 sm:space-y-6">

            <div

              className="relative group cursor-pointer"

              onClick={(e) => {

                e.preventDefault();

                e.stopPropagation();

                setModalImage(section.image);

              }}

              role="button"

              tabIndex={0}

              onKeyDown={(e) => {

                if (e.key === 'Enter' || e.key === ' ') {

                  e.preventDefault();

                  setModalImage(section.image);

                }

              }}

            >

              <div className="absolute inset-0 bg-black/20 rounded-xl sm:rounded-2xl md:rounded-3xl transform translate-x-1.5 sm:translate-x-2 md:translate-x-3 translate-y-1.5 sm:translate-y-2 md:translate-y-3 blur-lg sm:blur-xl md:blur-2xl opacity-50" />

              <div className="relative rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden border-2 sm:border-4 md:border-6 lg:border-8 border-white/30 shadow-2xl backdrop-blur-sm group-hover:border-white/40 transition-all duration-500">

                <ImageWithFallback

                  src={section.image}

                  className="w-full h-auto max-h-[300px] sm:max-h-[400px] md:max-h-[500px] lg:max-h-[600px] object-contain transform group-hover:scale-105 transition-transform duration-[3000ms] ease-out"

                  alt={section.title}

                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />



                {/* Click to enlarge indicator */}

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">

                  <div className="bg-white/20 backdrop-blur-md rounded-full p-2 sm:p-3 border border-white/30">

                    <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />

                    </svg>

                  </div>

                </div>

              </div>

            </div>



            {/* Additional Images Gallery */}

            {section.additionalImages && section.additionalImages.length > 0 && (

              <div className="space-y-2 sm:space-y-3 md:space-y-4">

                {section.additionalImages.map((image: string, imgIndex: number) => (

                  <motion.div

                    key={imgIndex}

                    initial={{ opacity: 0, scale: 0.9 }}

                    whileInView={{ opacity: 1, scale: 1 }}

                    viewport={{ once: true }}

                    transition={{ delay: imgIndex * 0.1, duration: 0.6 }}

                    className="relative group cursor-pointer"

                    onClick={(e) => {

                      e.preventDefault();

                      e.stopPropagation();

                      setModalImage(image);

                    }}

                  >

                    <div className="relative rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden border border-white/20 sm:border-2 md:border-3 shadow-xl backdrop-blur-sm group-hover:border-white/30 transition-all duration-300">

                      <ImageWithFallback

                        src={image}

                        className="w-full h-auto max-h-[200px] sm:max-h-[250px] md:max-h-[300px] lg:max-h-[350px] object-contain transform group-hover:scale-105 transition-transform duration-1000 ease-out"

                        alt={`${section.title} - View ${imgIndex + 2}`}

                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />



                      {/* Click to enlarge indicator */}

                      <div className="absolute top-1 right-1 sm:top-2 sm:right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">

                        <div className="bg-white/20 backdrop-blur-md rounded-full p-1.5 sm:p-2 border border-white/30">

                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />

                          </svg>

                        </div>

                      </div>

                    </div>

                  </motion.div>

                ))}

              </div>

            )}



            {/* Videos Section - Right below the images */}

            {section.videos && section.videos.length > 0 && (

              <div className="w-full">

                <VideoSection videos={section.videos} />

              </div>

            )}

          </div>

        </motion.div>

      </div>



      {/* Image Modal */}

      <ImageModal

        isOpen={!!modalImage}

        onClose={() => setModalImage(null)}

        src={modalImage || ""}

        alt={section.title}

      />

    </section>



  );

};



export const ProductDetail = ({ product, onBack, onContact }: ProductDetailProps) => {

  const productContent = getProductContent(product.title);

  const isAnuvadiniConnect = product.title === "Anuvadini Bharatt";



  useEffect(() => {

    // First scroll to top

    window.scrollTo({ top: 0, behavior: 'instant' });

    

    // Then check if there's a hash in the URL and scroll to that section

    const hash = window.location.hash.substring(1); // Remove the # symbol

    if (hash) {

      // Wait a bit for the page to render, then scroll to the section

      setTimeout(() => {

        const element = document.getElementById(hash);

        if (element) {

          element.scrollIntoView({ 

            behavior: 'smooth', 

            block: 'start',

            inline: 'nearest'

          });

        }

      }, 500); // Wait 500ms for the page to fully render

    }

  }, [product.title]); // Re-run when product changes



  return (

    <motion.div

      initial={{ opacity: 0 }}

      animate={{ opacity: 1 }}

      exit={{ opacity: 0 }}

      className="bg-gray-50 min-h-screen"

    >

      <section className={`pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-12 sm:pb-16 md:pb-20 px-3 sm:px-4 md:px-6 relative overflow-hidden bg-gradient-to-br ${product.gradient}`}>

          <motion.button

            initial={{ opacity: 0, x: -30 }}

            animate={{ opacity: 1, x: 0 }}

            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}

            onClick={onBack}

            className={`group flex items-center gap-1.5 sm:gap-2 md:gap-3 px-2.5 sm:px-3 md:px-5 py-1.5 sm:py-2 md:py-3 ${isAnuvadiniConnect ? 'bg-[#1e3a8a]/20 hover:bg-[#1e3a8a]/30 text-[#1e3a8a] border-[#1e3a8a]/30' : 'bg-white/15 hover:bg-white/25 text-white border-white/30'} rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 font-semibold backdrop-blur-xl border text-xs sm:text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 mb-6 sm:mb-8 md:mb-12`}

          >

            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 group-hover:-translate-x-2 transition-transform duration-300" />

            Back to Anuvadini Home Page

          </motion.button>

        <div className="absolute top-0 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-white/5 rounded-full -mr-36 sm:-mr-72 -mt-36 sm:-mt-72 blur-[60px] sm:blur-[120px]" />

        <div className="absolute bottom-0 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-black/5 rounded-full -ml-16 sm:-ml-32 -mb-16 sm:-mb-32 blur-[40px] sm:blur-[80px]" />



        <div className="max-w-7xl mx-auto relative z-10">

          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 sm:gap-6 md:gap-8 lg:gap-12">

            <motion.div

              initial={{ opacity: 0, scale: 0.8, rotate: -10 }}

              animate={{ opacity: 1, scale: 1, rotate: 0 }}

              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}

              className="relative group flex-shrink-0"

            >

              <div className="absolute inset-0 bg-gray-200/30 rounded-2xl sm:rounded-3xl blur-lg sm:blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative bg-gray-100 p-4 sm:p-6 md:p-8 lg:p-12 rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200/40 backdrop-blur-sm hover:shadow-3xl transition-all duration-500 group-hover:scale-105">

                <img src={product.logo} alt={`${product.title} Logo`} className="w-24 h-20 sm:w-32 sm:h-24 md:w-40 md:h-28 lg:w-60 lg:h-32 object-contain mx-auto" />

              </div>

            </motion.div>



            <div className="flex-1 space-y-3 sm:space-y-4 md:space-y-6 text-center lg:text-left">

              <motion.div

                initial={{ opacity: 0, y: 20 }}

                animate={{ opacity: 1, y: 0 }}

                transition={{ delay: 0.2, duration: 0.6 }}

                className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-5 py-1 sm:py-1.5 md:py-2.5 ${isAnuvadiniConnect ? 'bg-[#1e3a8a]/20 border-[#1e3a8a]/30 text-[#1e3a8a]' : 'bg-white/15 border-white/30 text-white'} backdrop-blur-xl rounded-full border text-[9px] sm:text-[10px] md:text-xs font-bold uppercase tracking-wider shadow-lg`}

              >

                <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${isAnuvadiniConnect ? 'bg-[#1e3a8a]/80' : 'bg-white/80'} rounded-full animate-pulse`} />

                Detailed Platform Insights

              </motion.div>



              <motion.h1

                initial={{ opacity: 0, y: 30 }}

                animate={{ opacity: 1, y: 0 }}

                transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}

                className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-black leading-[0.9] sm:leading-[0.85] tracking-tighter ${isAnuvadiniConnect ? 'text-[#1e3a8a]' : ' text-white'}`}

                style={{ textShadow: '0 8px 25px rgba(0,0,0,0.4)' }}

              >

                {product.title}

              </motion.h1>



              <motion.div

                initial={{ opacity: 0, y: 20 }}

                animate={{ opacity: 1, y: 0 }}

                transition={{ delay: 0.4, duration: 0.6 }}

                className={`text-xs sm:text-sm md:text-base lg:text-xl ${isAnuvadiniConnect ? 'text-black' : 'text-white/95'} max-w-4xl leading-relaxed font-medium text-justify-inter-word`}

              >

                {parseMarkdownText(productContent.overview || product.desc)}

              </motion.div>



              {product.list && (

                <motion.div

                  initial={{ opacity: 0, y: 20 }}

                  animate={{ opacity: 1, y: 0 }}

                  transition={{ delay: 0.5, duration: 0.6 }}

                  className="mt-4 sm:mt-6 md:mt-8 max-w-3xl"

                >

                  <div className={`${isAnuvadiniConnect ? 'bg-[#1e3a8a]/20 border-[#1e3a8a]/30' : 'bg-white/10 border-white/30'} rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 backdrop-blur-xl border shadow-xl`}>

                    <h3 className={`${isAnuvadiniConnect ? 'text-[#1e3a8a]' : 'text-white'} font-bold mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base flex items-center justify-center lg:justify-start gap-1.5 sm:gap-2`}>

                      <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 ${isAnuvadiniConnect ? 'bg-[#1e3a8a]' : 'bg-white'} rounded-full`} />

                      Key Solutions

                    </h3>

                    <ul className="space-y-1.5 sm:space-y-2 md:space-y-3">

                      {product.list.map((item, index) => (

                        <motion.li

                          key={index}

                          initial={{ opacity: 0, x: -20 }}

                          animate={{ opacity: 1, x: 0 }}

                          transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}

                          className="flex items-start gap-2 sm:gap-3 group"

                        >

                          <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 ${isAnuvadiniConnect ? 'text-[#1e3a8a]/80 group-hover:text-[#1e3a8a]' : 'text-white/80 group-hover:text-white'} flex-shrink-0 mt-0.5 transition-colors`} />

                          {item.href ? (

                            <a

                              href={item.href}

                              target="_blank"

                              rel="noopener noreferrer"

                              className={`${isAnuvadiniConnect ? 'text-black hover:text-[#1e3a8a]' : 'text-white/90 hover:text-white'} underline underline-offset-2 text-sm sm:text-base font-medium transition-colors duration-300 text-justify-inter-word`}

                            >

                              {item.label}

                            </a>

                          ) : (

                            <span className={`${isAnuvadiniConnect ? 'text-black' : 'text-white/90'} text-sm sm:text-base font-medium text-justify-inter-word`}>{item.label}</span>

                          )}

                        </motion.li>

                      ))}

                    </ul>

                  </div>

                </motion.div>

              )}







              <motion.div

                initial={{ opacity: 0, y: 20 }}

                animate={{ opacity: 1, y: 0 }}

                transition={{ delay: 0.8, duration: 0.6 }}

                className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4"

              >

                <button

                  onClick={onContact}

                  className="group bg-green-400 hover:bg-green-500 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-4 lg:py-5 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-xs sm:text-sm md:text-base lg:text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 w-full sm:w-auto"

                >

                  Contact Anuvadini

                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 group-hover:translate-x-1 transition-transform" />

                </button>

              </motion.div>

            </div>

          </div>

        </div>



        {/* Our Product Line section with navigation links */}

        {'navigationLinks' in productContent && productContent.navigationLinks && productContent.navigationLinks.length > 0 && (

          <motion.div

            initial={{ opacity: 0, y: 20 }}

            animate={{ opacity: 1, y: 0 }}

            transition={{ delay: 0.9, duration: 0.6 }}

            className="hidden sm:block mx-auto px-3 sm:px-4 md:px-6 mt-6 sm:mt-8 pb-6 sm:pb-8 pt-3 sm:pt-4 scrollbar-hide"

          >

            <h1 className={`${isAnuvadiniConnect ? 'text-[#1e3a8a]' : 'text-white'} text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-6`}>Our Product Line</h1>

            <div className="flex gap-3 sm:gap-4 md:gap-6 justify-center flex-wrap scrollbar-hide">

              {productContent.navigationLinks.map((link: any, index: number) => (

                <motion.a

                  key={link.id}

                  initial={{ opacity: 0, scale: 0.9 }}

                  animate={{ opacity: 1, scale: 1 }}

                  transition={{ delay: 1.0 + index * 0.05, duration: 0.4 }}

                  onClick={(e) => {

                    e.preventDefault();

                    const element = document.getElementById(link.section);

                    if (element) {

                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

                    }

                  }}

                  href={`#${link.section}`}

                  className={`${isAnuvadiniConnect ? 'text-[#1e3a8a] hover:text-[#1e40af] decoration-[#1e3a8a]/50 hover:decoration-[#1e3a8a]' : 'text-white hover:text-white/80 decoration-white/50 hover:decoration-white'} text-xs sm:text-sm md:text-base font-medium transition-all duration-300 hover:scale-105 cursor-pointer underline underline-offset-4`}

                >

                  {link.title}

                </motion.a>

              ))}

            </div>

          </motion.div>

        )}

      </section>



      <div className={`bg-gradient-to-br ${product.gradient}`}>

        {productContent.sections && productContent.sections.length > 0 ? (

          productContent.sections.map((section, index) => (

            <DetailSection key={section.id} section={section} index={index} onContact={onContact} />

          ))

        ) : (

          <div className="py-8 sm:py-12 md:py-16 lg:py-20 text-center space-y-3 sm:space-y-4 md:space-y-6">

            <div className={`w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 ${isAnuvadiniConnect ? 'bg-[#1e3a8a]/20 border-[#1e3a8a]/30 text-[#1e3a8a]/60' : 'bg-white/20 border-white/30 text-white/60'} backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mx-auto border`}>

              <Bot className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />

            </div>

            <h3 className={`text-lg sm:text-xl md:text-2xl font-bold ${isAnuvadiniConnect ? 'text-[#1e3a8a]/80' : 'text-white/80'}`}>Detailed Content Coming Soon</h3>

            <p className={`${isAnuvadiniConnect ? 'text-black/70' : 'text-white/70'} max-w-xl mx-auto text-xs sm:text-sm md:text-base text-justify-inter-word px-4`}>

              We're preparing comprehensive details and feature showcases for this platform.

              Contact us to learn more about current implementations and capabilities.

            </p>

            <button

              onClick={onContact}

              className="bg-green-400 hover:bg-green-500 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-sm sm:text-base md:text-lg transition-all shadow-lg hover:scale-105 active:scale-95 w-full sm:w-auto"

            >

              Contact Anuvadini

            </button>

          </div>

        )}

      </div>



      <section className={`py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br ${product.gradient} relative overflow-hidden`}>

        <div className="absolute inset-0 bg-black/20" />

        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 text-center relative z-10 space-y-4 sm:space-y-6 md:space-y-8">

          <motion.h2

            initial={{ opacity: 0, y: 20 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-tight ${isAnuvadiniConnect ? 'text-[#1e3a8a]' : 'text-white'}`}

            style={{ textShadow: '0 8px 25px rgba(0,0,0,0.3)' }}

          >

            Ready to deploy?

          </motion.h2>

          <motion.p

            initial={{ opacity: 0, y: 20 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            transition={{ delay: 0.1 }}

            className={`text-xs sm:text-sm md:text-base lg:text-lg ${isAnuvadiniConnect ? 'text-black/90' : 'text-white/90'} leading-relaxed max-w-2xl mx-auto text-justify-inter-word`}

          >

            Join hundreds of forward-thinking organizations already using {product.title} to transform their operations and reach billions of people globally.

          </motion.p>

          <motion.div

            initial={{ opacity: 0, y: 20 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            transition={{ delay: 0.2 }}

            className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 md:gap-4"

          >

            <button

              onClick={onContact}

              className="w-full sm:w-auto bg-green-400 hover:bg-green-500 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-4 lg:py-5 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-sm sm:text-base md:text-lg lg:text-xl transition-all shadow-xl hover:scale-105 active:scale-95"

            >

              Contact Anuvadini

            </button>

            <button

              onClick={onContact}

              className="w-full sm:w-auto bg-green-400 hover:bg-green-500 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-4 lg:py-5 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-sm sm:text-base md:text-lg lg:text-xl transition-all hover:scale-105 active:scale-95"

            >

              Schedule Demo

            </button>

          </motion.div>

        </div>

      </section>

    </motion.div>

  );

};